/**
 * useAccess — single source of truth for the new admin RBAC.
 *
 * Replaces the constellation of `useAuth().permissions`, `usePermission`,
 * `useEffectivePermissions`, `localStorage.menuStructure`, and the equivalence
 * map. Backed by the backend endpoint `GET /admin/me/access`.
 *
 * Lifecycle:
 *   - First load: fetch + populate context.
 *   - 60s interval: silent refresh (handles stale token cases).
 *   - Socket events `permission:changed`, `grant:added`, `grant:revoked`,
 *     `role:updated`: immediate refresh.
 *   - On reconnect: refresh.
 *
 * Super-admin: backend returns `permissions: ['*']`. The `has()` helper
 * treats `*` as "anything" — single source of truth for the rule
 * "super-admin sees everything".
 *
 * Returns a stable object so components can subscribe to specific fields
 * without re-rendering on every fetch.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import apiService from '../services/api';
import type { PermissionCode } from '../constants/permissionCatalog';

export type AdminShiftStatus =
  | 'on_shift' | 'off_shift' | 'on_break' | 'on_leave'
  | 'holiday' | 'no_profile' | 'inactive';

export interface ActiveGrant {
  _id: string;
  userId: string;
  permission: string;
  scope: 'always' | 'window' | 'once';
  category?: string | null;
  expiresAt?: string;
  reason?: string;
  /** Server-computed: usable at this very moment (window/consumed aware). */
  liveNow?: boolean;
}

export interface AccessSnapshot {
  user: { id: string; name?: string; email?: string; isSuperAdmin: boolean };
  permissions: string[];                  // from backend; '*' for super-admin
  activeGrants: ActiveGrant[];
  shift: { status: AdminShiftStatus; categories: string[] };
}

export interface UseAccessResult extends AccessSnapshot {
  /** True when at least one of the listed permissions is held (or super-admin). */
  has: (perm: PermissionCode | PermissionCode[]) => boolean;
  /** True when the user is currently on-shift OR is super-admin. */
  isOnShift: boolean;
  /** Forces a refetch — call after a known permission change in the same tab. */
  refresh: () => Promise<void>;
  /** Loading state (only for the very first fetch). */
  isLoading: boolean;
  /** Last error (network/HTTP). null on success. */
  error: string | null;
}

const REFRESH_INTERVAL_MS = 60_000;

/** Module-level cache so multiple components share one fetch. */
let cachedSnapshot: AccessSnapshot | null = null;
let inflightFetch: Promise<AccessSnapshot> | null = null;
const subscribers = new Set<() => void>();

async function fetchAccess(): Promise<AccessSnapshot> {
  if (inflightFetch) return inflightFetch;
  inflightFetch = (async () => {
    try {
      const res = await apiService.get<{
        success?: boolean;
        status?: string;
        data: AccessSnapshot;
      }>('/admin/me/access');
      // apiService may unwrap { status, data } → AccessSnapshot directly,
      // or return the whole envelope { success, data } depending on response
      // shape. Detect both.
      const snap: AccessSnapshot = (res as any)?.data?.user
        ? (res as { data: AccessSnapshot }).data
        : ((res as unknown) as AccessSnapshot);
      cachedSnapshot = snap;
      return snap;
    } finally {
      inflightFetch = null;
      subscribers.forEach((cb) => cb());
    }
  })();
  return inflightFetch;
}

export function useAccess(): UseAccessResult {
  const [, forceRender] = useState({});
  const mountedRef = useRef(true);

  // First-load fetch (only if cache is empty).
  const [isLoading, setIsLoading] = useState(!cachedSnapshot);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    mountedRef.current = true;

    const reRender = () => {
      if (mountedRef.current) forceRender({});
    };
    subscribers.add(reRender);

    if (!cachedSnapshot) {
      fetchAccess()
        .then(() => mountedRef.current && setIsLoading(false))
        .catch((e) => {
          if (mountedRef.current) {
            setError(e?.message || 'خطا در بارگذاری دسترسی');
            setIsLoading(false);
          }
        });
    }

    // Background refresh — silent.
    const interval = setInterval(() => {
      fetchAccess().catch(() => {/* swallow background errors */});
    }, REFRESH_INTERVAL_MS);

    // Socket-based instant invalidation. Picks up any global socket attached
    // at window.__figanoSocket; falls back to no-op if absent.
    let socketCleanup: (() => void) | undefined;
    try {
      const socket: any =
        (window as any).__figanoSocket || (window as any).socket || null;
      if (socket && typeof socket.on === 'function') {
        const events = [
          'permission:changed',
          'grant:added',
          'grant:revoked',
          'role:updated',
        ];
        const handler = () => fetchAccess().catch(() => {});
        for (const e of events) socket.on(e, handler);
        socketCleanup = () => {
          for (const e of events) socket.off?.(e, handler);
        };
      }
    } catch { /* socket optional */ }

    return () => {
      mountedRef.current = false;
      subscribers.delete(reRender);
      clearInterval(interval);
      socketCleanup?.();
    };
  }, []);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      await fetchAccess();
    } catch (e: any) {
      setError(e?.message || 'خطا در بارگذاری دسترسی');
    }
  }, []);

  const snap = cachedSnapshot ?? {
    user: { id: '', isSuperAdmin: false },
    permissions: [],
    activeGrants: [],
    shift: { status: 'no_profile' as AdminShiftStatus, categories: [] },
  };

  // Stable `has()` and `isOnShift` references per snapshot.
  const has = useMemo(() => {
    const set = new Set(snap.permissions);
    const wildcard = set.has('*');
    return (perm: PermissionCode | PermissionCode[]): boolean => {
      if (wildcard) return true;
      const arr = Array.isArray(perm) ? perm : [perm];
      return arr.some((p) => set.has(p));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snap.permissions]);

  const isOnShift = useMemo(
    () => snap.user.isSuperAdmin || snap.shift.status === 'on_shift',
    [snap.user.isSuperAdmin, snap.shift.status]
  );

  return {
    ...snap,
    has,
    isOnShift,
    refresh,
    isLoading,
    error,
  };
}

/**
 * Test-only escape hatch: lets unit tests prime the cache without a real fetch.
 * Production code MUST NOT call this.
 */
export function __setAccessCacheForTests(snap: AccessSnapshot | null): void {
  cachedSnapshot = snap;
  subscribers.forEach((cb) => cb());
}

/**
 * Module-level imperative API for code that lives outside React components
 * (AuthContext after login/logout, axios interceptor on 401, etc.).
 *
 * `triggerAccessRefresh()` invalidates the cache and fetches fresh data.
 * `clearAccessCache()` wipes the cache (use on logout to avoid the next
 * mount inheriting the previous user's permissions).
 */
export function triggerAccessRefresh(): Promise<AccessSnapshot> {
  cachedSnapshot = null;
  return fetchAccess();
}

export function clearAccessCache(): void {
  cachedSnapshot = null;
  inflightFetch = null;
  subscribers.forEach((cb) => cb());
}
